import {
	Box,
	Container,
	Heading,
	Stack,
	Text,
	Inline,
	Button,
	Component,
	Icons,
	SwitchInput,
	Card,
	CardBody,
	Grid,
	Col,
	RelativeTime,
} from "@sembark-travel/ui-base"
import { ChangePasswordForm } from "./ChangePassword"
import { TwoFactorAuth, WebAuths } from "./MFAs"
import { AuthSessionsList } from "./AuthSessions"
import { Dialog } from "@sembark-travel/ui-dialog"
import { useAuthUser, useHasFeatureFlag } from "../Auth"
import { ConnectedOAuthAppsList } from "../Integrations"
import { useXHR } from "@sembark-travel/xhr"
import { useSearchParams } from "react-router"
import { isTruthy } from "@sembark-travel/ui-form"
import { SIZES } from "@sembark-travel/ui-theme"

interface SettingsCardProps {
	iconName: keyof typeof Icons
	title: React.ReactNode
	description: string
	children: React.ReactNode
	iconSize?: keyof typeof SIZES
}

export function SecuritySettings() {
	const { user, fetchUser } = useAuthUser()
	const [searchParams, setSearchParams] = useSearchParams()
	const xhr = useXHR()
	const canUseOAuthApps = useHasFeatureFlag("oauth_apps")
	if (!user) return null
	const initChangePassword =
		isTruthy(searchParams.get("resetPassword")) &&
		!user?.password_login_disabled_at
	const mustChangePassword = user?.require_password_change

	return (
		<Container>
			<Stack gap="8" paddingY="8">
				<Stack gap="2">
					<Heading as="h1" fontSize="2xl" fontWeight="semibold">
						Security Settings
					</Heading>
					<Text color="muted" fontSize="base">
						Manage your account security and authentication methods
					</Text>
				</Stack>

				<SettingsCard
					iconName="Lock"
					title="Password"
					description="Use a strong, unique password to keep your account secure."
					key={initChangePassword ? 1 : 0}
				>
					<Stack>
						<Component initialState={false}>
							{({ state, setState }) => (
								<form>
									<Inline gap="2" as="label" justifyContent="end">
										<SwitchInput
											name="can_password_login"
											disabled={state}
											checked={!user.password_login_disabled_at}
											onChange={async (checked) => {
												if (
													!window.confirm(
														`Are you sure you want to ${
															checked ? "Enabled" : "Disable"
														} login via Password.${
															!checked
																? "\n\nOnly Admins will be able to enable it back."
																: ""
														}`
													)
												) {
													return
												}
												setState(true)
												try {
													await xhr.patch(
														`/users/${user.id}/toggle-password-login`
													)
													fetchUser(true)
												} catch (e) {
													const error = e as Error
													alert(
														error.message ||
															"Something went wrong. Please try after sometime."
													)
												}
												setState(false)
											}}
										/>
										<Text as="span">
											Password Login:{" "}
											{user.password_login_disabled_at ? "Disabled" : "Enabled"}
										</Text>
									</Inline>
								</form>
							)}
						</Component>
						<Stack gap="2">
							<Text color="muted" fontSize="sm">
								Current Password
								{user.password_updated_at ? (
									<>
										{" "}
										(Updated{" "}
										<RelativeTime timestamp={user.password_updated_at} />)
									</>
								) : null}
							</Text>
							<Inline
								gap="4"
								paddingY="2"
								paddingX="4"
								alignItems="center"
								borderWidth="1"
								rounded="md"
								bgColor="default"
							>
								<Text color="muted">•••••••••••</Text>
							</Inline>
						</Stack>
						{!user.password_login_disabled_at ? (
							<Component initialState={isTruthy(initChangePassword)}>
								{({ state, setState }) => (
									<>
										<Box width="auto" marginTop="4">
											<Button onClick={() => setState(true)} status="primary">
												<Icons.Pencil />
												Change Password
											</Button>
										</Box>

										<Dialog sm open={state} onClose={() => setState(false)}>
											<Dialog.Header
												closeButton={!isTruthy(mustChangePassword)}
												title="Change Password"
											/>
											<Dialog.Body>
												<ChangePasswordForm
													onSuccess={() => {
														if (initChangePassword) {
															setSearchParams({ resetPassword: "0" })
														}
														setState(false)
													}}
													onCancel={() => {
														if (mustChangePassword) {
															alert("You MUST change password to continue.")
															return
														}
														if (initChangePassword) {
															setSearchParams({ resetPassword: "0" })
														}
														setState(false)
													}}
												/>
											</Dialog.Body>
										</Dialog>
									</>
								)}
							</Component>
						) : null}
					</Stack>
				</SettingsCard>
				<SettingsCard
					iconName="EmptyShield"
					title="Two Factor Authentication"
					description="Two-factor authentication adds an additional layer of security to your account by requiring more than just a password to log in."
				>
					<TwoFactorAuth user={user} />
				</SettingsCard>
				<SettingsCard
					iconName="KeyRound"
					title="Password-Less Authenticators"
					description="Sign in securely without passwords using your trusted device or app."
				>
					<WebAuths />
				</SettingsCard>
				<SettingsCard
					iconName="Desktop"
					title="Active Sessions and Devices"
					description="Manage your active sessions on other browsers and devices."
				>
					<AuthSessionsList />
				</SettingsCard>
				{canUseOAuthApps ? (
					<SettingsCard
						iconName="ShieldCheck"
						title="Connected Apps"
						description="Apps you approved to work with Sembark on your behalf, such as Claude or ChatGPT."
					>
						<ConnectedOAuthAppsList />
					</SettingsCard>
				) : null}
			</Stack>
		</Container>
	)
}

export function SettingsCard({
	iconName,
	title,
	description,
	children,
	iconSize = "5",
}: SettingsCardProps) {
	return (
		<Card marginBottom="0">
			<CardBody style={{ padding: "0" }}>
				<Grid>
					<Col xs={12} md={4} bgColor="subtle" paddingY="4">
						<Stack gap="1">
							<Inline gap="3" alignItems="center">
								<Box height="10" display="flex" alignItems="center">
									{!!iconName &&
										(() => {
											const IconComponent = Icons[iconName]
											return IconComponent ? (
												<IconComponent size={iconSize} />
											) : null
										})()}
								</Box>
								<Heading as="h3" fontSize="lg" fontWeight="semibold">
									{title}
								</Heading>
							</Inline>
							<Box paddingLeft="8">
								<Text color="muted" fontSize="sm">
									{description}
								</Text>
							</Box>
						</Stack>
					</Col>
					<Col xs={12} md={8} paddingY="6">
						{children}
					</Col>
				</Grid>
			</CardBody>
		</Card>
	)
}
